import fetch from '@/utils/fetch'

const googleApiBaseUrl = 'https://maps.googleapis.com/maps/api'

const getCurrentPosition = (): Promise<GeolocationPosition> => {
  if (typeof navigator !== 'undefined' && 'geolocation' in navigator) {
    return new Promise((resolve, reject) =>
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        timeout: 10000,
      })
    )
  }
  return Promise.reject(new Error('Geolocation not supported'))
}

const getAddressValue = ({
  components,
  names = [],
  longName = true,
  countryCode,
}: MapAddress.GetAddressValueParams): string | undefined => {
  return (
    names
      .map((type) =>
        components.find((com) => com.types.some((t) => t === type))
      )
      .filter((x): x is MapAddress.AddressComponent => x !== undefined)
      .map((com) => {
        if (
          com.types.includes('street_number') &&
          countryCode === 'TW' &&
          !com.long_name.includes('號')
        ) {
          return `${com.long_name}號`
        }
        return com[longName ? 'long_name' : 'short_name']
      })
      .join(' ') || undefined
  )
}

const getMapApi = ({
  mapServices,
  apiKey,
  language,
  country: _country,
  countryCode: _countryCode,
  mapper: _mapper,
  getMapper = null,
}: MapAddress.GetMapApiParams) => {
  // Ensure mapServices and places API are available
  if (!mapServices || !mapServices.places) {
    throw new Error(
      'Google Maps Places API is not loaded yet. Please ensure the API is fully initialized before calling getMapApi.'
    )
  }

  const transformResponseAddress = (
    address: MapAddress.Address,
    withCountryCode = false
  ): MapAddress.TransformedAddress => {
    const components = address.address_components
    const { lat, lng } = address.geometry.location
    const { mapper, country, countryCode } =
      typeof getMapper === 'function'
        ? getMapper(address)
        : {
            mapper: _mapper,
            country: _country,
            countryCode: _countryCode,
          }

    const transformedAddress: MapAddress.TransformedAddress = {
      formattedAddress: address.formatted_address,
      addressOne:
        getAddressValue({
          components,
          names: mapper?.addressOne,
          countryCode,
        }) || '',
      city:
        getAddressValue({ components, names: mapper?.city, countryCode }) || '',
      state:
        getAddressValue({ components, names: mapper?.state, countryCode }) ||
        '',
      postal:
        getAddressValue({ components, names: mapper?.postal, countryCode }) ||
        '',
      country:
        country ||
        getAddressValue({ components, names: mapper?.country, countryCode }) ||
        '',
      latitude: typeof lat === 'function' ? lat() : lat,
      longitude: typeof lng === 'function' ? lng() : lng,
    }

    if (country !== 'Taiwan') {
      transformedAddress.addressTwoDetails = {
        buildingName: address.name || '',
        unitNumber: '-',
        addressType: 'building',
      }
    }

    if (withCountryCode) {
      transformedAddress.countryCode =
        countryCode ||
        getAddressValue({
          components,
          names: mapper?.country,
          longName: false,
          countryCode,
        })
    }

    return transformedAddress
  }

  // Using the new Place API instead of deprecated PlacesService

  const sessionToken = new mapServices.places.AutocompleteSessionToken()

  const googleGeocodeBaseUrl = `${googleApiBaseUrl}/geocode/json?key=${apiKey}`

  const googleTimezoneBaseUrl = `${googleApiBaseUrl}/timezone/json?key=${apiKey}`

  const localisationQuery = `language=${language}&region=${_countryCode}`

  const transformAddresses = (
    addresses: MapAddress.Address[]
  ): MapAddress.TransformedAddress[] =>
    addresses.map((address) => transformResponseAddress(address))

  const getPlaceDetails = async (
    placeId: string,
    withCountryCode = false
  ): Promise<MapAddress.TransformedAddress> => {
    const place = new mapServices.places.Place({
      id: placeId,
    })
    await place.fetchFields({
      fields: [
        'addressComponents',
        'formattedAddress',
        'location',
        'displayName',
        'id',
      ],
    })

    // Transform new Place API response to match expected format
    const transformedPlace: MapAddress.Address = {
      address_components:
        place.addressComponents?.map(
          (component: MapAddress.AddressComponentNew) => ({
            long_name: component.longText,
            short_name: component.shortText,
            types: component.types,
          })
        ) || [],
      formatted_address: place.formattedAddress || '',
      geometry: {
        location: {
          lat: () => place.location?.lat() || 0,
          lng: () => place.location?.lng() || 0,
        },
      },
      name: place.displayName,
      place_id: place.id,
    }

    return transformResponseAddress(transformedPlace, withCountryCode)
  }

  const fetchLocationByGeometricCenter = async ({
    latitude,
    longitude,
  }: {
    latitude: number
    longitude: number
  }): Promise<MapAddress.TransformedAddress[]> => {
    const { results } = await fetch(
      `${googleGeocodeBaseUrl}&latlng=${latitude},${longitude}&${localisationQuery}&location_type=GEOMETRIC_CENTER`,
      {}
    )
    // remove plus code address -> more detail about plus code https://maps.google.com/pluscodes/
    const filterResults = results.filter(
      (item: MapAddress.Address) =>
        !Object.prototype.hasOwnProperty.call(item, 'plus_code')
    )
    return results && transformAddresses(filterResults)
  }

  const isPostalCodeExist = (
    addressComponent: MapAddress.AddressComponent[]
  ): boolean =>
    addressComponent.some((item) => item.types.includes('postal_code'))

  const reverseGeoCode = async ({
    latitude,
    longitude,
  }: {
    latitude: number
    longitude: number
  }): Promise<MapAddress.TransformedAddress[]> => {
    const { results } = await fetch(
      `${googleGeocodeBaseUrl}&latlng=${latitude},${longitude}&${localisationQuery}&location_type=ROOFTOP`,
      {}
    )
    if (results.length === 0) return []
    const addressComponent = results[0].address_components
    if (isPostalCodeExist(addressComponent)) {
      return results && transformAddresses(results)
    }
    return fetchLocationByGeometricCenter({ latitude, longitude })
  }

  const getCurrentAddress =
    async (): Promise<MapAddress.TransformedAddress | null> => {
      try {
        const position = await getCurrentPosition()
        const results = await reverseGeoCode({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        })
        return results[0] || null
      } catch {
        return null
      }
    }

  const getTimezoneFromLatLon = async (
    latitude: number,
    longitude: number
  ): Promise<number | null> => {
    try {
      const timestamp = Math.floor(new Date().getTime() / 1000)
      const { rawOffset } = await fetch(
        `${googleTimezoneBaseUrl}&location=${latitude},${longitude}&timestamp=${timestamp}`,
        {}
      )
      const gmtOffset = rawOffset / 3600

      if (isNaN(gmtOffset)) {
        return null
      }

      return gmtOffset
    } catch {
      return null
    }
  }

  const findPlaceFromQuery = async (
    input: string,
    {
      componentRestrictions = { country: _countryCode },
      withCountryCode = false,
    }: {
      componentRestrictions?: { country?: string }
      withCountryCode?: boolean
    } = {}
  ): Promise<
    Array<{
      formattedAddress: string
      fetchDetails: () => Promise<MapAddress.TransformedAddress>
    }>
  > => {
    try {
      const request: MapAddress.AutocompleteSuggestionRequest = {
        input,
        sessionToken,
        includedRegionCodes: componentRestrictions.country
          ? [componentRestrictions.country]
          : undefined,
      }

      const response =
        await mapServices.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          request
        )

      return response.suggestions.map(({ placePrediction }) => ({
        formattedAddress: placePrediction.text.text,
        fetchDetails: () =>
          getPlaceDetails(placePrediction.placeId, withCountryCode),
      }))
    } catch (error) {
      console.error('AutocompleteSuggestion error:', error)
      throw error
    }
  }

  return {
    reverseGeoCode,
    getCurrentAddress,
    getTimezoneFromLatLon,
    findPlaceFromQuery,
    getPlaceDetails,
  }
}

export default getMapApi
